import { useCallback, useEffect, useRef } from 'react';
import { useAnalytics } from './useAnalytics';

interface ConversionFunnelStep {
  step: string;
  timestamp: number;
  data?: any;
}

interface ConversionGoal {
  name: string;
  value: number;
  currency?: string;
}

export const useConversionTracking = () => {
  const { trackEvent } = useAnalytics();
  const funnelSteps = useRef<ConversionFunnelStep[]>([]);
  const conversionGoals = useRef<ConversionGoal[]>([]);
  const sessionId = useRef<string>(Math.random().toString(36).substring(7));

  // Funnel Tracking
  const trackFunnelStep = useCallback((step: string, data?: any) => {
    const funnelStep: ConversionFunnelStep = {
      step,
      timestamp: Date.now(),
      data
    };
    
    funnelSteps.current.push(funnelStep);

    trackEvent('funnel_step', {
      funnel_step: step,
      step_number: funnelSteps.current.length,
      session_id: sessionId.current,
      step_data: JSON.stringify(data),
      time_from_start: funnelSteps.current.length > 1 
        ? Date.now() - funnelSteps.current[0].timestamp 
        : 0,
      time_from_previous: funnelSteps.current.length > 1 
        ? Date.now() - funnelSteps.current[funnelSteps.current.length - 2].timestamp 
        : 0,
      event_category: 'conversion_funnel'
    });
  }, [trackEvent]);

  // Conversion Goal Tracking
  const trackConversionGoal = useCallback((goalName: string, value: number, currency: string = 'USD') => {
    const goal: ConversionGoal = { name: goalName, value, currency };
    conversionGoals.current.push(goal);

    trackEvent('conversion_goal', {
      goal_name: goalName,
      goal_value: value,
      currency: currency,
      session_id: sessionId.current,
      total_session_value: conversionGoals.current.reduce((sum, g) => sum + g.value, 0),
      goals_completed: conversionGoals.current.length,
      event_category: 'conversion'
    });

    // Track as enhanced e-commerce event
    trackEvent('purchase', {
      transaction_id: `${sessionId.current}_${Date.now()}`,
      value: value,
      currency: currency,
      items: [{
        item_id: goalName,
        item_name: goalName,
        category: 'conversion_goal',
        quantity: 1,
        price: value
      }]
    });
  }, [trackEvent]);

  // Micro-conversion Tracking
  const trackMicroConversion = useCallback((action: string, value: number = 1) => {
    trackEvent('micro_conversion', {
      action: action,
      value: value,
      session_id: sessionId.current,
      timestamp: Date.now(),
      event_category: 'micro_conversion'
    });
  }, [trackEvent]);

  // Attribution Tracking
  const trackAttribution = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const attribution = {
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      utm_term: urlParams.get('utm_term'),
      utm_content: urlParams.get('utm_content'),
      referrer: document.referrer,
      landing_page: window.location.pathname,
      session_id: sessionId.current
    };

    trackEvent('attribution_tracking', {
      ...attribution,
      event_category: 'attribution'
    });

    return attribution;
  }, [trackEvent]);

  // Exit Intent Tracking
  const trackExitIntent = useCallback((trigger: 'mouse_leave' | 'scroll_up' | 'tab_switch') => {
    const currentFunnelStep = funnelSteps.current.length > 0 
      ? funnelSteps.current[funnelSteps.current.length - 1].step 
      : 'unknown';

    trackEvent('exit_intent', {
      trigger: trigger,
      current_funnel_step: currentFunnelStep,
      steps_completed: funnelSteps.current.length,
      time_on_site: funnelSteps.current.length > 0 
        ? Date.now() - funnelSteps.current[0].timestamp 
        : 0,
      session_id: sessionId.current,
      event_category: 'user_behavior'
    });
  }, [trackEvent]);

  // Conversion Optimization Tracking
  const trackElementVisibility = useCallback((elementId: string, isVisible: boolean) => {
    trackEvent('element_visibility', {
      element_id: elementId,
      is_visible: isVisible,
      timestamp: Date.now(),
      viewport_height: window.innerHeight,
      scroll_position: window.scrollY,
      event_category: 'conversion_optimization'
    });
  }, [trackEvent]);

  // CTA Performance Tracking
  const trackCTAPerformance = useCallback((ctaId: string, action: 'view' | 'click', context?: string) => {
    trackEvent('cta_performance', {
      cta_id: ctaId,
      action: action,
      context: context,
      session_id: sessionId.current,
      timestamp: Date.now(),
      event_category: 'cta_tracking'
    });
  }, [trackEvent]);

  // Initialize exit intent detection
  useEffect(() => {
    let mouseLeaveTimer: NodeJS.Timeout;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        clearTimeout(mouseLeaveTimer);
        mouseLeaveTimer = setTimeout(() => {
          trackExitIntent('mouse_leave');
        }, 1000); // Delay to avoid false triggers
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        trackExitIntent('tab_switch');
      }
    };

    let lastScrollTop = 0;
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (scrollTop < lastScrollTop && scrollTop < 100) {
        trackExitIntent('scroll_up');
      }
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Track initial attribution
    trackAttribution();

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(mouseLeaveTimer);
    };
  }, [trackAttribution, trackExitIntent]);

  return {
    trackFunnelStep,
    trackConversionGoal,
    trackMicroConversion,
    trackAttribution,
    trackExitIntent,
    trackElementVisibility,
    trackCTAPerformance,
    getFunnelData: () => funnelSteps.current,
    getConversionGoals: () => conversionGoals.current,
    getSessionId: () => sessionId.current
  };
};